import React, { useMemo, useState } from 'react';

const POST_IDEAS = [
  {
    category: 'Before & after',
    title: 'Show a real before-and-after result',
    idea: 'Share a consented before-and-after photo that shows the progress electrolysis can make over time. Keep lighting, angle, and distance as consistent as possible, and explain that results vary by person.',
    why: 'A real visual helps a prospective client understand the process and picture what a consultation could clarify for them.',
    callToAction: 'Curious what a consultation could look like for you? Book a free consultation.',
    format: 'Photo carousel or side-by-side image',
  },
  {
    category: 'First visit',
    title: 'Take the mystery out of a first consultation',
    idea: 'Walk through what a new client can expect at a free consultation, from arriving to asking questions and discussing their goals.',
    why: 'Knowing what will happen can make reaching out feel less intimidating for someone who is new to electrolysis.',
    callToAction: 'Have questions before getting started? Book a free consultation.',
    format: 'Short talking video or 3-slide carousel',
  },
  {
    category: 'Education',
    title: 'Answer one common electrolysis question',
    idea: 'Choose a question you often hear and answer it in plain language. Keep the answer helpful and invite people to ask about their own situation.',
    why: 'Useful answers help people discover your page while showing that you welcome questions.',
    callToAction: 'Leave your question below or book a free consultation to talk it through.',
    format: 'FAQ graphic or short video',
  },
  {
    category: 'Behind the scenes',
    title: 'Introduce the person behind the service',
    idea: 'Share a warm photo or short clip introducing Kyana and one thing you want every first-time client to know about feeling welcome.',
    why: 'People often want to know who they will meet before they book a personal service.',
    callToAction: 'I’d love to meet you—book a free consultation when you’re ready.',
    format: 'Photo with a short personal caption',
  },
  {
    category: 'Client experience',
    title: 'Share a client win—with permission',
    idea: 'With the client’s explicit permission, share a short, anonymous reflection about what felt easier after they understood the process. Avoid identifying details or promises about results.',
    why: 'A genuine client perspective can help a new visitor feel understood and build trust.',
    callToAction: 'Want to see whether electrolysis may fit your goals? Start with a free consultation.',
    format: 'Quote card or text post',
  },
  {
    category: 'Local community',
    title: 'Make a local introduction post',
    idea: 'Introduce your Conyers-area practice and mention the nearby communities you welcome. Invite locals to share the questions they have about electrolysis.',
    why: 'A clear local connection can help nearby people recognize that the service is accessible to them.',
    callToAction: 'Local to the area? Book a free consultation at Key Electrolysis Services.',
    format: 'Friendly photo or local-themed graphic',
  },
  {
    category: 'Education',
    title: 'Explain what makes electrolysis distinct',
    idea: 'Create a simple, neutral explainer about electrolysis and how it differs from other hair-removal approaches. Keep it factual and avoid putting other methods down.',
    why: 'People comparing options may save or share a clear explanation that helps them make an informed choice.',
    callToAction: 'Not sure which questions to ask? Bring them to a free consultation.',
    format: 'Comparison carousel with concise, factual points',
  },
  {
    category: 'Booking',
    title: 'Explain the easiest way to get started',
    idea: 'Show the booking page and explain the first step: choosing a free consultation time online.',
    why: 'A clear next step removes friction for people who are interested but have not booked yet.',
    callToAction: 'Choose a time for your free consultation through the link in our bio.',
    format: 'Screen recording with captions',
  },
  {
    category: 'Trust & comfort',
    title: 'Answer a question about comfort',
    idea: 'Share a considerate, non-judgmental response to a question clients may have about comfort. Explain that experiences differ and invite them to discuss concerns with you.',
    why: 'Acknowledging a common concern can help someone feel safer asking for guidance.',
    callToAction: 'You can bring any concerns to your free consultation.',
    format: 'Question card and short answer',
  },
  {
    category: 'Studio',
    title: 'Give a calm tour of the treatment space',
    idea: 'Show a few welcoming, tidy details of the treatment room and explain how you prepare the space for an appointment, without showing client information.',
    why: 'Seeing the environment ahead of time can make a first visit feel more familiar.',
    callToAction: 'Would you like to visit? Book a free consultation.',
    format: 'Short vertical video',
  },
  {
    category: 'FAQ',
    title: 'Explain how to prepare for an appointment',
    idea: 'Share a short checklist of what clients can do before an appointment, based on your own professional guidance.',
    why: 'Practical information is easy to save and gives potential clients a useful reason to follow.',
    callToAction: 'Save this for later, and contact us if you have questions.',
    format: 'Saveable checklist graphic',
  },
  {
    category: 'Engagement',
    title: 'Invite followers to choose the next FAQ',
    idea: 'Offer two or three topics you can explain next, such as what happens at a consultation, how to book, or what questions to ask.',
    why: 'A simple prompt encourages interaction and helps you learn what prospective clients want to know.',
    callToAction: 'Comment with the topic you’d like me to cover next.',
    format: 'Story poll or feed question',
  },
  {
    category: 'Service education',
    title: 'Explain why a consultation is a helpful first step',
    idea: 'Describe how a consultation gives someone time to discuss goals, ask questions, and learn what to expect—without pressure to decide immediately.',
    why: 'A low-pressure invitation can help people who are interested but unsure about booking a treatment.',
    callToAction: 'Start with a free consultation and bring your questions.',
    format: 'Carousel or welcoming talking video',
  },
  {
    category: 'Myth-busting',
    title: 'Gently clarify one misconception',
    idea: 'Pick a misconception you hear about electrolysis and respond with a respectful, evidence-conscious explanation. If the answer depends on the person, say so.',
    why: 'Clear, balanced education can build credibility and prompt people to seek personalized information.',
    callToAction: 'Have a question about your situation? Let’s talk at a consultation.',
    format: 'Myth / fact carousel',
  },
];

const getLocalDayNumber = (date) => {
  const localMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const yearStart = new Date(date.getFullYear(), 0, 0);
  return Math.floor((localMidnight - yearStart) / 86400000);
};

export default function SocialIdeas() {
  const [alternateOffset, setAlternateOffset] = useState(0);
  const [copied, setCopied] = useState(false);
  const today = useMemo(() => new Date(), []);
  const dayNumber = getLocalDayNumber(today);
  const ideaIndex = (dayNumber + alternateOffset) % POST_IDEAS.length;
  const idea = POST_IDEAS[ideaIndex];
  const formattedDate = today.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  const copyIdea = async () => {
    const text = `${idea.title}\n\nIdea: ${idea.idea}\n\nWhy it can help: ${idea.why}\n\nSuggested call to action: ${idea.callToAction}\n\nSuggested format: ${idea.format}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'absolute';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <main className="mx-auto max-w-6xl space-y-6">
      <header className="rounded-2xl bg-primary p-6 text-white shadow-md sm:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-secondary">Your simple marketing nudge</p>
        <div className="mt-3 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <h1 className="text-3xl font-bold sm:text-4xl">Today’s Social Post Idea</h1>
            <p className="mt-2 max-w-2xl text-sm text-white/80">One practical idea to help more people discover Key Electrolysis Services—no full caption required.</p>
          </div>
          <p className="shrink-0 rounded-full bg-white/10 px-4 py-2 text-sm font-medium">{formattedDate}</p>
        </div>
      </header>

      <section className="grid gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(280px,0.8fr)]">
        <article className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="rounded-full bg-secondary px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">{idea.category}</span>
            <span className="text-sm text-gray-500">Idea {ideaIndex + 1} of {POST_IDEAS.length}</span>
          </div>
          <h2 className="mt-5 text-2xl font-bold text-primary">{idea.title}</h2>
          <div className="mt-6 space-y-5">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">The idea</h3>
              <p className="mt-2 leading-7 text-gray-700">{idea.idea}</p>
            </div>
            <div className="rounded-xl bg-tertiary p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-primary">How it can help bring in new clients</h3>
              <p className="mt-2 leading-6 text-gray-700">{idea.why}</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Suggested call to action</h3>
              <p className="mt-2 font-medium text-primary">{idea.callToAction}</p>
            </div>
            <p className="text-sm text-gray-500"><span className="font-semibold text-gray-700">Suggested format:</span> {idea.format}</p>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <button type="button" onClick={copyIdea} className="rounded-lg bg-primary px-4 py-2.5 font-semibold text-white transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
              {copied ? 'Copied!' : 'Copy idea'}
            </button>
            <button type="button" onClick={() => { setAlternateOffset((value) => (value + 1) % POST_IDEAS.length); setCopied(false); }} className="rounded-lg border border-gray-300 px-4 py-2.5 font-semibold text-primary transition hover:bg-tertiary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
              Show another idea
            </button>
          </div>
        </article>

        <aside className="space-y-6">
          <section className="rounded-2xl border border-amber-200 bg-amber-50 p-6 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-800">On your list soon</p>
            <h2 className="mt-3 text-xl font-bold text-primary">Before-and-after post</h2>
            <p className="mt-3 leading-6 text-gray-700">You mentioned you’re preparing one. When it’s ready, a real progress photo can help prospective clients understand what to expect.</p>
            <div className="mt-4 rounded-xl bg-white/80 p-4">
              <p className="text-sm font-semibold text-gray-800">Quick prep checklist</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-5 text-gray-700">
                <li>Get the client’s explicit permission to share.</li>
                <li>Use similar lighting, angle, and distance.</li>
                <li>Avoid filters or edits that change the result.</li>
                <li>Don’t promise that everyone will get the same outcome.</li>
              </ul>
            </div>
            <p className="mt-4 text-sm font-medium text-amber-900">No rush—use this when your photos and permission are ready.</p>
          </section>

          <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
            <h2 className="text-lg font-bold text-primary">A helpful posting rhythm</h2>
            <p className="mt-2 text-sm leading-6 text-gray-600">Mix education, personal introductions, client questions, and booking reminders. A useful post can build trust even when it doesn’t directly advertise a service.</p>
            <p className="mt-3 text-xs leading-5 text-gray-500">These ideas rotate locally by calendar day. They are curated in the app, not generated by AI, and nothing is published automatically.</p>
          </section>
        </aside>
      </section>
    </main>
  );
}
